import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  toast,
  Text,
  Tooltip,
  Dialog,
  DialogContent,
  IconButton,
  Input,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  FormLabel
} from '@sparrowengg/twigs-react';
import {
  CloseIcon,
  DeleteIcon,
  EllipsisVerticalIcon,
  SaveIcon
} from '@sparrowengg/twigs-react-icons';
import FileUploads from './file-uploads';
import AppDetails from './app-details';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { AddAppsProps } from '../types';
import {
  CUSTOM_APP,
  PUBLIC_APP,
  youtubeRegex,
  defaultValues,
  appTypeConstants
} from '../constants';
import { devPortalServices, uploadFileToS3 } from 'commons/services';
import { developedAppTypes } from 'features/code-editor/constants';
import CodeIcon from 'commons/icons/code';

const AddApps = ({ app }: AddAppsProps) => {
  const navigate = useNavigate();
  const {
    register,
    setValue,
    handleSubmit,
    formState: { errors, dirtyFields, isDirty },
    watch,
    reset,
    getValues
  } = useForm({
    defaultValues
  });
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const [disabled, setIsDisabled] = useState({ publish: true, update: false });
  const [deleteButtonDisabled, setDeleteButtonDisabled] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteBtnLoader, setDeleteBtnLoader] = useState(false);
  const [showReleaseRequestModal, setShowReleaseRequestModal] = useState(false);
  const [releaseRequestBtnLoader, setReleaseRequestBtnLoader] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<any>(null);

  const isSubmitDisabled =
    disabled.publish || disabled.update || loading || releaseRequestBtnLoader;

  const zipFileName = searchParams.get('fileName');

  const convertUrl = (url: string): string => {
    if (youtubeRegex.test(url)) {
      const videoIdMatch = url.match(youtubeRegex);
      const videoId = videoIdMatch ? videoIdMatch[4] : null;
      if (videoId) {
        return `https://www.youtube.com/embed/${videoId}`;
      }
    }
    return url;
  };

  const getUploadedZipFile = () => {
    const storedBlob = sessionStorage.getItem('downloadedZipBlob');

    if (storedBlob && zipFileName) {
      const binaryData = atob(storedBlob);
      const array = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        array[i] = binaryData.charCodeAt(i);
      }
      const blob = new Blob([array], { type: 'application/zip' });

      const file = new File([blob], zipFileName, { type: 'application/zip' });
      setValue('zipFile', file, {
        shouldDirty: true
      });

      // Clean up
      sessionStorage.removeItem('downloadedZipBlob');
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('fileName');
    }
  };

  // Helper function to get file extension
  const getFileExtension = (file: File): string => {
    const name = file.name;
    const lastDot = name.lastIndexOf('.');
    if (lastDot === -1) return '';
    return name.substring(lastDot + 1).toLowerCase();
  };

  const hasZipChange = () => {
    const zipFile = getValues('zipFile');
    return zipFile && zipFile instanceof File && zipFile.size > 0;
  };

  const submitHandler = async (value: any) => {
    const status = app?.releaseRequestStatus?.toUpperCase?.() || '';
    const isDeploymentInProgress = status.startsWith('DEPLOYMENT');

    const skipDelete =
      status === 'CLOSED' || status === 'DONE' || status === '';
    const zipChanged = hasZipChange();

    // Block if deployment already started
    if (isDeploymentInProgress) {
      toast({
        variant: 'error',
        description:
          'Cannot update the app. Deployment has already begun. Please contact the admin.'
      });
      return;
    }

    // If a prior release request exists and status is not deployment, prompt to delete before update
    if (!isDeploymentInProgress && zipChanged && !skipDelete) {
      setPendingFormData(value);
      setShowReleaseRequestModal(true);
      return;
    }

    await performUpdate(value);
  };

  const handleReleaseRequestConfirm = async () => {
    setReleaseRequestBtnLoader(true);
    try {
      if (!pendingFormData) {
        setShowReleaseRequestModal(false);
        return;
      }

      if (app?.releaseRequestId) {
        await devPortalServices.deleteReleaseRequest(app.releaseRequestId);
      }

      await performUpdate(pendingFormData);
      setShowReleaseRequestModal(false);
      setPendingFormData(null);
    } catch (err: any) {
      console.error(err);
      toast({
        variant: 'error',
        description: err?.message || 'Failed to proceed'
      });
    } finally {
      setReleaseRequestBtnLoader(false);
    }
  };

  // No page-load modal; prompt only during update flow.

  const performUpdate = async (value: any) => {
    setLoading(true);
    try {
      value.videoUrl = convertUrl(value.videoUrl);

      if (app) {
        // Edit app flow - PUT to update-app
        const appId = app.appId;
        let zipS3Url: string | null = null;
        let iconS3Url: string | null = null;

        // Check if zip file is attached/changed
        const zipFile = getValues('zipFile');
        const isZipChanged =
          zipFile && zipFile instanceof File && zipFile.size > 0;

        // Check if icon/logo is changed (new File uploaded, not the existing URL string)
        const icon = getValues('icon');
        let iconFile: File | null = null;

        // Determine if icon is a new file (File instance) vs existing URL
        if (icon instanceof File) {
          iconFile = icon;
        } else if (icon && typeof icon === 'object' && 'data' in icon) {
          // If data is a File, it's a new upload
          if (icon.data instanceof File) {
            iconFile = icon.data;
          }
          // If data is a string (URL), it's the existing icon - skip upload
        }

        // Only get signed URLs if files are being uploaded
        if (isZipChanged || iconFile) {
          // Get icon extension if icon is being uploaded
          const iconExtension = iconFile
            ? getFileExtension(iconFile)
            : undefined;

          // Get signed URLs for file uploads
          const signedUrlResponse =
            await devPortalServices.getUploadSignedUrls(iconExtension);

          if (!signedUrlResponse) {
            throw new Error('Failed to get signed URLs');
          }

          const { signedAppIconUploadUrl, signedAppZipUploadUrl } =
            signedUrlResponse;

          // Upload zip file if changed
          if (isZipChanged && signedAppZipUploadUrl) {
            zipS3Url = await uploadFileToS3(zipFile, signedAppZipUploadUrl);
          }

          // Upload icon if changed
          if (iconFile && signedAppIconUploadUrl) {
            iconS3Url = await uploadFileToS3(iconFile, signedAppIconUploadUrl);
          }
        }

        // Prepare payload for app update (PATCH)
        const updatePayload: any = {
          zipFile: zipS3Url || null,
          icon: iconS3Url || null,
          appName: value.appName || null,
          shortDescription: value.shortDescription || null,
          appInstructions: value.appInstructions || null,
          videoUrl: value.videoUrl || null,
          appId: appId,
          appVersionId: null,
          websiteLink: value.websiteLink || null,
          appOverView: value.appOverView || null,
          supportUrl: value.supportUrl || null,
          supportEmail: value.supportEmail || null,
          codeType: zipFileName ? developedAppTypes.SAME_VERSION : null,
          isNewApp: false
        };

        await devPortalServices.updateApp(updatePayload);
        toast({
          variant: 'success',
          description: 'App Request has been raised successfully'
        });
        navigate('/');
      } else {
        // New app creation flow
        // Step 1: Get zip file and validate
        const zipFile = getValues('zipFile');
        if (!zipFile || !(zipFile instanceof File) || !zipFile.size) {
          throw new Error(
            'Zip file is required. Please upload a zip file before submitting.'
          );
        }

        // Step 2: Get icon file and its extension
        const icon = getValues('icon');
        let iconFile: File | null = null;
        let iconExtension: string | undefined;

        if (icon instanceof File) {
          iconFile = icon;
          iconExtension = getFileExtension(icon);
        } else if (icon && typeof icon === 'object' && 'data' in icon) {
          if (icon.data instanceof File) {
            iconFile = icon.data;
            iconExtension = getFileExtension(icon.data);
          }
        }

        // Step 3: Get signed URLs for file uploads
        const signedUrlResponse =
          await devPortalServices.getUploadSignedUrls(iconExtension);

        if (!signedUrlResponse) {
          throw new Error('Failed to get signed URLs');
        }

        const { signedAppIconUploadUrl, signedAppZipUploadUrl } =
          signedUrlResponse;

        if (!signedAppZipUploadUrl) {
          throw new Error('Failed to get zip signed URL');
        }

        // Step 4: Upload zip file to S3
        const zipS3Url = await uploadFileToS3(zipFile, signedAppZipUploadUrl);

        // Step 5: Upload icon to S3 if provided
        let iconS3Url: string | null = null;
        if (iconFile && signedAppIconUploadUrl) {
          iconS3Url = await uploadFileToS3(iconFile, signedAppIconUploadUrl);
        }

        // Step 6: Create app with all data (POST)
        const createPayload: any = {
          zipFile: zipS3Url || null,
          icon: iconS3Url || null,
          appName: value.appName || null,
          shortDescription: value.shortDescription || null,
          appInstructions: value.appInstructions || null,
          videoUrl: value.videoUrl || null,
          appVersionId: null,
          websiteLink: value.websiteLink || null,
          appOverView: value.appOverView || null,
          supportUrl: value.supportUrl || null,
          supportEmail: value.supportEmail || null,
          codeType: zipFileName ? developedAppTypes.SAME_VERSION : null,
          isNewApp: true
        };

        await devPortalServices.createApp(createPayload);

        toast({
          variant: 'success',
          description: 'App has been uploaded successfully'
        });
        navigate('/');
      }
    } catch (err: any) {
      console.error(err);
      // Extract error message from API response
      let errorMessage = 'Error in file upload, Please try again';

      if (err?.response?.data?.error) {
        // If the API returns an error object with an error field
        errorMessage = err.response.data.error;
      } else if (err?.response?.data?.message) {
        // If the API returns an error object with a message field
        errorMessage = err.response.data.message;
      } else if (err?.message) {
        // If it's a standard error with a message
        errorMessage = err.message;
      }

      toast({
        variant: 'error',
        description: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteApp = async () => {
    setDeleteBtnLoader(true);
    try {
      await devPortalServices.deleteSingleApp(app?.appId as number);
      navigate('/');
      toast({
        variant: 'default',
        description: `${app?.name} has been deleted successfully.`
      });
    } catch (err) {
      console.error(err);
    } finally {
      setDeleteBtnLoader(false);
    }
  };

  useEffect(() => {
    if (app) {
      const {
        name,
        description,
        howToUse,
        icon,
        appType,
        videoUrl,
        websiteLink,
        appOverView,
        supportEmail,
        supportUrl
      } = app;
      const formValues = {
        appName: name,
        shortDescription: description,
        appInstructions: howToUse,
        icon: { data: icon },
        videoUrl,
        supportUrl,
        appOverView,
        supportEmail,
        websiteLink
      };
      reset({
        ...formValues
      });
      if (!!zipFileName) {
        getUploadedZipFile();
      }
    } else {
      reset(defaultValues);
    }
  }, [app]);

  return (
    <Flex justifyContent="center" css={{ marginTop: '$24' }}>
      <Box css={{ maxWidth: 1200, width: '100%' }}>
        <form onSubmit={handleSubmit(submitHandler)}>
          <Flex
            alignItems="center"
            justifyContent="space-between"
            css={{
              paddingBlock: '$5'
            }}
          >
            <Flex flexDirection="column" gap="$2">
              <Heading size="h5">App Information</Heading>
              <Text size="md" css={{ color: '$neutral800' }}>
                Enter all the required fields to publish the app
              </Text>
            </Flex>
            <Flex alignItems="center" gap="$4">
              <Button
                size="lg"
                type="submit"
                loading={loading || releaseRequestBtnLoader}
                disabled={isSubmitDisabled}
                rightIcon={<SaveIcon />}
                css={{ marginRight: '$4' }}
              >
                {app ? 'Update Changes' : 'Save Changes'}
              </Button>
              <Box
                css={{
                  borderLeft: '$borderWidths$xs solid $neutral200',
                  height: '$6',
                  width: '1px'
                }}
              />
              {app ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <IconButton
                      icon={<EllipsisVerticalIcon />}
                      size="lg"
                      variant="ghost"
                      color="default"
                    />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    css={{ minWidth: 200 }}
                    align="end"
                    sideOffset={4}
                  >
                    {app.appStatus === appTypeConstants.DRAFT && (
                      <DropdownMenuItem>
                        <Flex
                          alignItems="center"
                          gap="$4"
                          css={{ width: '100%', cursor: 'pointer' }}
                          onClick={() =>
                            navigate(`/code-editor?app=${app?.appVersionId}`)
                          }
                        >
                          <CodeIcon size={24} color="#6a6a6a" />
                          Continue Editing
                        </Flex>
                      </DropdownMenuItem>
                    )}
                    <Tooltip
                      hidden={!deleteButtonDisabled}
                      content="App installed in one or more accounts cannot be deleted. Please contact support for more details."
                      size="md"
                      side="right"
                    >
                      {/* wrapper for tooltip */}
                      <Flex css={{ width: '100%' }}>
                        <DropdownMenuItem
                          css={{ width: '100%' }}
                          disabled={deleteButtonDisabled}
                        >
                          <Flex
                            alignItems="center"
                            gap="$4"
                            onClick={() => setShowDeleteModal(true)}
                            css={{ width: '100%', cursor: 'pointer' }}
                          >
                            <DeleteIcon size={24} color="#6a6a6a" />
                            Delete App
                          </Flex>
                        </DropdownMenuItem>
                      </Flex>
                    </Tooltip>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : null}
            </Flex>
          </Flex>
          <Flex css={{ paddingBlock: '$20' }}>
            <FileUploads
              getValues={getValues}
              setValue={setValue}
              zipFileName={zipFileName ?? ''}
            />
            <AppDetails
              app={app}
              loading={loading}
              register={register}
              errors={errors}
              watch={watch}
              isDirty={isDirty}
              setValue={setValue}
              getValues={getValues}
              setIsDisabled={setIsDisabled}
              setDeleteButtonDisabled={setDeleteButtonDisabled}
            />
          </Flex>
        </form>
      </Box>
      {showDeleteModal && (
        <WarningModal
          resetModal={() => {
            setShowDeleteModal(false);
          }}
          btnLoader={deleteBtnLoader}
          onClickHandler={() => handleDeleteApp()}
          appName={app?.name ?? ''}
        />
      )}
      {showReleaseRequestModal && (
        <ReleaseRequestConfirmModal
          resetModal={() => {
            setShowReleaseRequestModal(false);
            setPendingFormData(null);
          }}
          btnLoader={releaseRequestBtnLoader}
          onClickHandler={handleReleaseRequestConfirm}
          appName={app?.name ?? ''}
        />
      )}
    </Flex>
  );
};

export default AddApps;

const ReleaseRequestConfirmModal = ({
  resetModal,
  btnLoader,
  onClickHandler,
  appName
}: {
  resetModal: () => void;
  btnLoader?: boolean;
  onClickHandler: () => void;
  appName: string;
}) => {
  const [isDisabled, setIsDisabled] = useState(true);

  return (
    <Dialog open>
      <DialogContent css={{ padding: 0, borderRadius: '$xl', width: 476 }}>
        <Flex
          css={{
            padding: '$10 $12',
            borderBottom: '$borderWidths$xs solid $neutral200'
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading size="h6">Delete Release Request</Heading>
          <IconButton
            size="md"
            variant="ghost"
            color="default"
            icon={<CloseIcon />}
            onClick={() => resetModal()}
          />
        </Flex>
        <Flex flexDirection="column" gap="$14" css={{ padding: '$12' }}>
          <Flex flexDirection="column" gap="$4">
            <Text size="sm">
              To delete the release request, type the app name{' '}
              <Text
                as="span"
                size="sm"
                css={{ color: '$secondary400', whiteSpace: 'break-spaces' }}
                weight="bold"
              >
                {appName}
              </Text>
            </Text>
            <Input
              size="lg"
              onChange={(e) => {
                e.preventDefault();
                const inputValue = e?.currentTarget?.value?.trim() || '';
                const appNameNormalized = appName?.trim() || '';
                if (inputValue === appNameNormalized) {
                  setIsDisabled(false);
                } else {
                  setIsDisabled(true);
                }
              }}
            />
            <FormLabel>The app name must be case-sensitive</FormLabel>
          </Flex>
          <Text size="md">
            <Box as="strong">Note:</Box> This app has a pending release request.
            To make changes, the existing request needs to be deleted first.
          </Text>
          <Text size="md">
            Are you sure you want to delete the release request?
          </Text>
        </Flex>
        <Flex
          alignItems="center"
          gap="$6"
          css={{
            padding: '$12',
            paddingTop: 0,
            button: {
              flex: 1
            }
          }}
        >
          <Button
            color="error"
            size="xl"
            onClick={() => resetModal()}
            disabled={btnLoader}
          >
            No, Go Back
          </Button>
          <Button
            color="secondary"
            size="xl"
            onClick={() => onClickHandler()}
            disabled={isDisabled || btnLoader}
            loading={btnLoader}
          >
            Confirm
          </Button>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};

const WarningModal = ({
  resetModal,
  btnLoader,
  onClickHandler,
  appName
}: {
  resetModal: () => void;
  btnLoader?: boolean;
  onClickHandler: () => void;
  appName: string;
}) => {
  const [isDisabled, setIsDisabled] = useState(true);

  return (
    <Dialog open>
      <DialogContent css={{ padding: 0, borderRadius: '$xl', width: 476 }}>
        <Flex
          css={{
            padding: '$10 $12',
            borderBottom: '$borderWidths$xs solid $neutral200'
          }}
          alignItems="center"
          justifyContent="space-between"
        >
          <Heading size="h6">App Deletion Confirmation</Heading>
          <IconButton
            size="md"
            variant="ghost"
            color="default"
            icon={<CloseIcon />}
            onClick={() => resetModal()}
          />
        </Flex>
        <Flex flexDirection="column" gap="$14" css={{ padding: '$12' }}>
          <Flex flexDirection="column" gap="$4">
            <Text size="sm">
              To delete, type the app name{' '}
              <Text
                as="span"
                size="sm"
                css={{ color: '$secondary400', whiteSpace: 'break-spaces' }}
                weight="bold"
              >
                {appName}
              </Text>
            </Text>
            <Input
              size="lg"
              onChange={(e) => {
                e.preventDefault();
                const inputValue = e?.currentTarget?.value?.trim() || '';
                const appNameNormalized = appName?.trim() || '';
                if (inputValue === appNameNormalized) {
                  setIsDisabled(false);
                } else {
                  setIsDisabled(true);
                }
              }}
            />
            <FormLabel>The app name must be case-sensitive</FormLabel>
          </Flex>
          <Text size="md">
            <Box as="strong">Note:</Box> The deletion will remove all the App
            history and it can’t be revived.
          </Text>
          <Text size="md">Are you sure you want to delete the app?</Text>
        </Flex>
        <Flex
          alignItems="center"
          gap="$6"
          css={{
            padding: '$12',
            paddingTop: 0,
            button: {
              flex: 1
            }
          }}
        >
          <Button
            color="error"
            size="xl"
            onClick={() => resetModal()}
            disabled={btnLoader}
          >
            No, Go Back
          </Button>
          <Button
            color="secondary"
            size="xl"
            onClick={() => onClickHandler()}
            disabled={isDisabled || btnLoader}
            loading={btnLoader}
          >
            Yes, I am sure
          </Button>
        </Flex>
      </DialogContent>
    </Dialog>
  );
};
